import { useState, useCallback, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'src/trpc';
import type { ConfigurationTypeEnum, PricingRule } from '@libs/schemas';
import { debounce } from 'lodash';
import { useToast } from 'src/components/useToast';
import { useOrganization } from 'src/context/OrganizationContext';

interface UsePricingRulesOptions {
  page?: number;
  limit?: number;
  active?: boolean;
  search?: string;
  ruleType?: string;
  effectiveOn?: string;
}

interface OptimisticRule extends PricingRule {
  _isOptimistic?: boolean;
  _tempId?: string;
  _pendingOperation?: 'create' | 'update' | 'delete';
}

interface ValidationResult {
  isValid: boolean;
  conflicts: Array<{
    ruleId: string;
    ruleName: string;
    conflictType: string;
    message: string;
  }>;
  warnings: Array<{
    code: string;
    message: string;
    severity: 'info' | 'warning' | 'error';
  }>;
}

export function usePricingRules(options: UsePricingRulesOptions = {}) {
  const { organizationId, isResolved } = useOrganization();
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  // Local state for optimistic updates and migration
  const [optimisticRules, setOptimisticRules] = useState<OptimisticRule[]>([]);
  // Main query for pricing rules
  const rulesQuery = trpc.admin.pricingRuleManagement.list.useQuery(
    {
      page: options.page || 1,
      limit: options.limit || 50,
      active: options.active,
      search: options.search,
      ruleType: options.ruleType as ConfigurationTypeEnum,
      effectiveOn: options.effectiveOn as string,
    },
    {
      enabled: isResolved && !!organizationId,
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData, // Keep previous data while loading new data for better UX
    },
  );

  // Real-time validation query
  const [validationInput, setValidationInput] = useState<
    (Partial<PricingRule> & { excludeRuleId?: string }) | null
  >(null);
  const validationQuery = trpc.admin.pricingRuleManagement.validate.useQuery(
    validationInput as Parameters<typeof trpc.admin.pricingRuleManagement.validate.useQuery>[0],
    {
      enabled: !!validationInput && !!organizationId,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  // Debounced validation function
  const debouncedValidate = useMemo(
    () =>
      debounce((ruleData: Partial<PricingRule>, excludeRuleId?: string) => {
        if (ruleData && organizationId) {
          setValidationInput({
            ...ruleData,
            excludeRuleId,
          });
        }
      }, 300),
    [organizationId],
  );

  // Create mutation with optimistic updates
  const createMutation = trpc.admin.pricingRuleManagement.create.useMutation({
    onMutate: async (newRule) => {
      // Cancel any outgoing refetches
      await utils.admin.pricingRuleManagement.list.cancel();

      // Apply optimistic update
      const optimisticRule: OptimisticRule = {
        ...newRule,
        _isOptimistic: true,
        _pendingOperation: 'create',
      };

      setOptimisticRules((prev) => [optimisticRule, ...prev]);
      return { optimisticRule };
    },
    onError: (error, _variables, context) => {
      // Rollback optimistic update
      if (context?.optimisticRule._tempId) {
        setOptimisticRules((prev) =>
          prev.filter((r) => r._tempId !== context.optimisticRule._tempId),
        );
      }
      errorToast({
        title: 'Failed to create rule',
        description: error.message,
      });
    },
    onSuccess: (result, _variables, context) => {
      // Remove optimistic update
      if (context?.optimisticRule._tempId) {
        setOptimisticRules((prev) =>
          prev.filter((r) => r._tempId !== context.optimisticRule._tempId),
        );
      }
      // Invalidate and refetch
      utils.admin.pricingRuleManagement.list.invalidate();
      successToast({
        title: 'Rule created',
        description: `Successfully created rule "${result.name}"`,
      });
    },
  });

  // Update mutation with optimistic updates and version conflict handling
  const updateMutation = trpc.admin.pricingRuleManagement.update.useMutation({
    onMutate: async (updatedRule) => {
      await utils.admin.pricingRuleManagement.list.cancel();

      // Apply optimistic update
      setOptimisticRules((prev) => {
        const index = prev.findIndex((r) => r._id === updatedRule._id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            ...updatedRule,
            _pendingOperation: 'update',
          };
          return updated;
        }
        return prev;
      });

      return { originalRule: updatedRule };
    },
    onError: (error, _variables, _context) => {
      // Handle version conflicts specifically
      if (error.message.includes('version conflict')) {
        errorToast({
          title: 'Version Conflict',
          description: 'This rule was modified by another user. Please refresh and try again.',
        });
      } else {
        errorToast({
          title: 'Failed to update rule',
          description: error.message,
        });
      }

      // Rollback optimistic update
      utils.admin.pricingRuleManagement.list.invalidate();
    },
    onSuccess: (result, variables) => {
      // Remove optimistic updates for this rule
      setOptimisticRules((prev) => prev.filter((r) => r._id !== variables._id));
      utils.admin.pricingRuleManagement.list.invalidate();
      successToast({
        title: 'Rule updated',
        description: `Successfully updated rule "${result.name}"`,
      });
    },
  });

  // Delete mutation
  const deleteMutation = trpc.admin.pricingRuleManagement.delete.useMutation({
    onMutate: async ({ ruleId }) => {
      await utils.admin.pricingRuleManagement.list.cancel();

      // Apply optimistic update
      setOptimisticRules((prev) =>
        prev.map((rule) =>
          rule._id === ruleId ? { ...rule, _pendingOperation: 'delete' as const } : rule,
        ),
      );

      return { ruleId };
    },
    onError: (error, _variables, context) => {
      // Rollback optimistic update
      setOptimisticRules((prev) =>
        prev.map((rule) =>
          rule._id === context?.ruleId ? { ...rule, _pendingOperation: undefined } : rule,
        ),
      );
      errorToast({
        title: 'Failed to delete rule',
        description: error.message,
      });
    },
    onSuccess: (_result, variables) => {
      // Remove from optimistic rules
      setOptimisticRules((prev) => prev.filter((r) => r._id !== variables.ruleId));
      utils.admin.pricingRuleManagement.list.invalidate();
      successToast({
        title: 'Rule deleted',
        description: 'Successfully deleted rule',
      });
    },
  });

  // Duplicate mutation
  const duplicateMutation = trpc.admin.pricingRuleManagement.duplicate.useMutation({
    onSuccess: (result) => {
      utils.admin.pricingRuleManagement.list.invalidate();
      successToast({
        title: 'Rule duplicated',
        description: `Successfully created duplicate "${result.name}"`,
      });
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to duplicate rule',
        description: error.message,
      });
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = trpc.admin.pricingRuleManagement.bulkDelete.useMutation({
    onSuccess: (result) => {
      utils.admin.pricingRuleManagement.list.invalidate();
      successToast({
        title: 'Bulk delete completed',
        description: `Successfully deleted ${result.successful} rules`,
      });
    },
    onError: (error) => {
      errorToast({
        title: 'Bulk delete failed',
        description: error.message,
      });
    },
  });

  // Merge server data with optimistic updates
  const mergedRules = useMemo(() => {
    const serverRules = rulesQuery.data?.rules || [];

    // Filter out optimistic rules that are being deleted
    const validOptimisticRules = optimisticRules.filter(
      (rule) => rule._pendingOperation !== 'delete',
    );

    // Merge optimistic creates with server rules
    const optimisticCreates = validOptimisticRules.filter(
      (rule) => rule._pendingOperation === 'create',
    );

    // Apply optimistic updates to existing server rules
    const updatedServerRules = serverRules.map((serverRule) => {
      const optimisticUpdate = validOptimisticRules.find(
        (opt) => opt._id === serverRule._id && opt._pendingOperation === 'update',
      );
      return optimisticUpdate || serverRule;
    });

    return [...optimisticCreates, ...updatedServerRules] as PricingRule[];
  }, [rulesQuery.data?.rules, optimisticRules]);

  // Helper functions that maintain the original API
  const addRule = useCallback(
    (newRule: Partial<PricingRule>) => {
      // Validate configuration structure
      const configuration = newRule.configuration;
      if (!configuration) {
        throw new Error('Rule must have a configuration');
      }

      // Helper function to convert date strings to ISO datetime strings
      const toISODateTime = (
        dateString: string | undefined,
        defaultValue?: string,
      ): string | undefined => {
        if (!dateString) return defaultValue;

        // If already a datetime string, return as-is
        if (dateString.includes('T')) return dateString;

        // Convert date string (YYYY-MM-DD) to datetime string
        try {
          const date = new Date(dateString + 'T00:00:00.000Z');
          return date.toISOString();
        } catch {
          return defaultValue;
        }
      };

      // Default to current date for effectiveFrom, leave effectiveTo optional
      const defaultEffectiveFrom = new Date().toISOString();

      const rule = {
        ...newRule,
        name: newRule.name || '',
        configuration,
        scope: newRule.scope || {},
        precedence: newRule.precedence || 100,
        effectiveFrom: toISODateTime(newRule.effectiveFrom, defaultEffectiveFrom)!,
        effectiveTo: toISODateTime(newRule.effectiveTo),
        canCombine: newRule.canCombine || false,
        organizationId: newRule.organizationId || organizationId || '',
        active: newRule.active !== undefined ? newRule.active : true,
      };

      createMutation.mutate(rule);
      return rule; // Return for compatibility, though the real rule will come from the server
    },
    [createMutation, organizationId],
  );

  const updateRule = useCallback(
    (updatedRule: PricingRule) => {
      // Validate configuration structure
      const configuration = updatedRule.configuration;
      if (!configuration) {
        throw new Error('Rule must have a configuration');
      }

      // Helper function to convert date strings to ISO datetime strings
      const toISODateTime = (
        dateString: string | undefined,
        defaultValue?: string,
      ): string | undefined => {
        if (!dateString) return defaultValue;

        // If already a datetime string, return as-is
        if (dateString.includes('T')) return dateString;

        // Convert date string (YYYY-MM-DD) to datetime string
        try {
          const date = new Date(dateString + 'T00:00:00.000Z');
          return date.toISOString();
        } catch {
          return defaultValue;
        }
      };

      // Default to current date for effectiveFrom, leave effectiveTo optional
      const defaultEffectiveFrom = new Date().toISOString();

      const updateData = {
        _id: updatedRule._id!,
        name: updatedRule.name,
        configuration,
        scope: updatedRule.scope,
        precedence: updatedRule.precedence,
        effectiveFrom: toISODateTime(updatedRule.effectiveFrom, defaultEffectiveFrom)!,
        effectiveTo: toISODateTime(updatedRule.effectiveTo),
        canCombine: updatedRule.canCombine,
        organizationId: updatedRule.organizationId,
        active: updatedRule.active,
      };

      updateMutation.mutate(updateData);
    },
    [updateMutation],
  );

  const deleteRule = useCallback(
    (ruleId: string) => {
      deleteMutation.mutate({ ruleId });
    },
    [deleteMutation],
  );

  const duplicateRule = useCallback(
    (rule: PricingRule) => {
      const newName = `${rule.name} (Copy)`;
      duplicateMutation.mutate({ ruleId: rule._id!, newName });

      // Return a temporary rule for compatibility
      return {
        ...rule,
        _id: `temp-${Date.now()}`,
        name: newName,
      };
    },
    [duplicateMutation],
  );

  const clearAllRules = useCallback(() => {
    const allRuleIds = mergedRules.map((rule) => rule._id!);
    if (allRuleIds.length > 0) {
      bulkDeleteMutation.mutate({ ruleIds: allRuleIds });
    }
  }, [bulkDeleteMutation, mergedRules]);

  // Validation functions
  const validateRule = useCallback(
    (ruleData: Partial<PricingRule>, excludeRuleId?: string) => {
      debouncedValidate(ruleData, excludeRuleId);
    },
    [debouncedValidate],
  );

  // Initialize with empty rules - no sample data
  const initializeSampleRules = useCallback(() => {
    // No sample rules - users can create their own
  }, []);

  const isLoading = rulesQuery.isLoading;

  // Enhanced return object with new features
  return {
    // Original API - maintain compatibility
    rules: mergedRules,
    isLoading,
    addRule,
    updateRule,
    deleteRule,
    duplicateRule,
    clearAllRules,
    initializeSampleRules,

    // Enhanced features

    // Validation
    validateRule,
    validation: validationQuery.data as ValidationResult | undefined,
    isValidating: validationQuery.isFetching,

    // Metadata
    totalCount: rulesQuery.data?.total || 0,
    hasNextPage:
      rulesQuery.data?.page &&
      rulesQuery.data?.totalPages &&
      rulesQuery.data.page < rulesQuery.data.totalPages,

    // Mutation states for UI feedback
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    isBulkDeleting: bulkDeleteMutation.isPending,

    // Error states
    error: rulesQuery.error,

    // Manual refetch
    refetch: rulesQuery.refetch,
  };
}
